import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {OneMonthFreeMode, OneMonthFreeStep} from "../../one-month-free.component";
import {UserSubscription, UserSubscriptionCategory} from "../../../../../../user/user-subscription";
import {PaymentInfoComponent} from "../../../../payment-info/payment-info.component";
import {FormControl} from "@angular/forms";
import {DbValidators} from "../../../../../validators/DbValidators";
import {CouponCode} from "../../../../../../workspace/billing/coupon";
import {AuthService} from "../../../../../../auth/auth.service";
import {first} from "rxjs/operators";
import {CouponCodeMapper} from "../../../../../../workspace/billing/coupon-mapper";
import {Config} from "../../../../../../config/config";
import {User} from "../../../../../../user/user";
import {ChosenPlan} from "../../../../payment-info/models/plan.model";
import {GoogleAnalyticsService} from "../../../../../../shared/services/google-analytics/google-analytics.service";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {DbNumberFormatPipe} from "../../../../../../shared/pipes/db-number-ceil.pipe";

@Component({
    selector: 'db-one-month-free-checkout-demo',
    templateUrl: './one-month-free-checkout-demo.component.html',
    styleUrls: ['./one-month-free-checkout-demo.component.scss'],
    standalone: false
})
export class OneMonthFreeCheckoutDemoComponent implements OnInit, OnChanges {

  @ViewChild(PaymentInfoComponent) paymentInfo: PaymentInfoComponent;
  @Input() mode: OneMonthFreeMode;
  @Input() demoUser?: boolean;
  @Input() singlePlanMode: boolean = false;
  @Input() couponPercent: number;
  @Input() couponDays: number;
  @Input() hasCampaignCoupon = false;
  @Input() isAgency: boolean = false;
  @Input() isInvited: boolean = false;
  @Input() userSubscription: UserSubscription;
  @Input() user: User;
  @Input() chosenPlan: ChosenPlan;
  @Input() couponCode: string;
  @Input() isPaid?: boolean = false;
  @Input() paidDesc?: string;
  @Input() expirationDate?: string;
  @Output() onStepChange = new EventEmitter<OneMonthFreeStep>();
  @Output() onClose = new EventEmitter<void | 'upgraded'>();
  @Output() onBack = new EventEmitter();

  public loading = false;
  public couponLoading = false;
  public couponSectionToggled = false;
  public couponCodeInput = new FormControl('', [DbValidators.trim]);
  public coupon: CouponCode;
  public newPrice: number = 0;
  public Config = Config;
  public payPalInvalidCoupons = Config.couponsList.slice(1);
  public isPaypal = false;
  public annuallyPaymentDate = new Date();
  public monthlyPaymentDate = new Date();
  public isMobile = window.innerWidth <= 991;
  public points: string[] = [
    'Free for %7%, cancel any time',
    'We’ll remind you 24 hours before the period ends'
  ];
  public UserSubscriptionCategory = UserSubscriptionCategory;
  public DbNumberFormatPipe = DbNumberFormatPipe;

  private readonly _destroyPendingRequests$ = new Subject<void>();

  constructor(
    private readonly _authService: AuthService,
    private _googleAnalyticsService: GoogleAnalyticsService
  ) {
  }

  ngOnInit() {
    if (!this.couponCode) {
      if (localStorage.getItem('dbCouponFromEmail')) {
        this.couponCode = localStorage.getItem('dbCouponFromEmail');
      } else if (!this.isPaid) {
        const couponCode = '1wf';
        this.applyCoupon(false, couponCode);
      }
    }
    if (!!this.couponCode && this.hasCampaignCoupon && !this.isPaid) {
      if (this.couponCode !== '1wf' &&
        (!localStorage.getItem('dbCouponFromEmail') ||
          (localStorage.getItem('dbCouponFromEmail') && this.couponCode !== localStorage.getItem('dbCouponFromEmail')))) {
        this.couponSectionToggled = true;
        this.couponCodeInput.setValue(this.couponCode);
      }
      this.applyCoupon(false, this.couponCode);
    }
    this._getAnnuallyPaymentDate();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['chosenPlan'] && changes['chosenPlan'].currentValue) {
      this._destroyPendingRequests$.next();

      this.chosenPlan = changes['chosenPlan'].currentValue;
      this.newPrice = 0;

      if (!!this.couponCode && this.singlePlanMode) {
        this.applyCoupon(false, this.couponCode);
      }
      this._getAnnuallyPaymentDate();
    }
  }

  public deleteCoupon(reset = true) {
    this._sendEvent();
    this.couponCodeInput.reset();
    if (reset) {
      const coupon = localStorage.getItem('dbCouponFromEmail') || '1wf';
      this.applyCoupon(reset, coupon);
    }
  }

  public applyCoupon(reset = false, autoApply?): void {
    const coupon = autoApply ? autoApply : this.couponCodeInput.value;
    this.couponLoading = true;

    this._destroyPendingRequests$.next();

    this._authService.applyCoupon(this.chosenPlan.id, coupon, "")
      .pipe(
        first(response => !!response, null),
        takeUntil(this._destroyPendingRequests$)
      )
      .subscribe({
        next: (response) => {
          reset && (this.newPrice = 0);
          this.couponLoading = false;

          if (this.chosenPlan?.category !== UserSubscriptionCategory.DedicatedVM ||
              (this.chosenPlan?.category === UserSubscriptionCategory.DedicatedVM && !response?.data?.free_days_before_payment)) {

            if (coupon.indexOf(Config.popupCouponPrefixForAutoApply) > -1 || !!response?.data?.free_days_before_payment) {
              this.couponCode = coupon;
              this.couponCodeInput.setErrors(null);
              this.coupon = CouponCodeMapper.map(response.data);
              this.newPrice = response.data && response.data.new_price;
                const date = new Date();
                date.setDate(date.getDate() + response?.data?.free_days_before_payment);
                this.expirationDate = date.toDateString();
            } else {
              this.deleteCoupon(false);
              this.couponCodeInput.setErrors({'invalidCode': true});
            }
          }
        },
        error: () => {
          this.couponLoading = false;
          this.newPrice = 0;
          this.deleteCoupon(false);
          this.couponCodeInput.setErrors({'invalidCode': true});
        }
      });
  }

  public changeSubscrId(): void {
    localStorage.setItem('dbCheckoutSubscriptionId', this.chosenPlan.id.toString());
  }

  public handlePaymentIsPaypal(isPaypal: boolean) {
    this.isPaypal = isPaypal;
  }

  private _getAnnuallyPaymentDate(): void {
    const today = new Date();
    this.annuallyPaymentDate = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate() + 7);
    this.monthlyPaymentDate = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate() + 7);
  }

  private _sendEvent(): void {
    const analyticsData = {
      eventCategory: "Dashboard Action",
      eventAction: "Remove Coupon",
      eventLabel: this.coupon?.couponCode
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  ngOnDestroy() {
    this._destroyPendingRequests$.next();
    this._destroyPendingRequests$.complete();
  }

}
